import {booleanQueryParam} from '../../../common/utils/booleanQueryParam';
import {I18n} from '../../../common/translator/i18n';
import {SHOP_DISCOUNT_FLOW} from '../constants';

export const AUTH_PATH_CORE = '/services/login_with_shop/authorize';

/**
 * Builds the Authorize URL to use for the ULC.
 * @param {object} params The root parameter object.
 * @param {string} params.apiKey The app's api key.
 * @param {string} params.analyticsTraceId Links analytics events together in one flow.
 * @param {string} params.storefrontOrigin The domain of the storefront page that the web component is rendered on.
 * @param {boolean} params.devMode Dev mode flag.
 * @param {boolean} params.phoneCapture Phone capture flag. If `true`, the component is configured to ask for phone consent.
 *   Otherwise, the component is configured to ask for email consent only.
 * @param {string} params.saveDiscountAt The step at which the discount should be saved. We care mostly about the "never"
 *   setting since not all clients are allowed to use it.
 * @param {string} params.flowVersion The version/variant of the flow, for analytics purposes.
 * @returns {string} The Authorize URL
 */
export const buildAuthorizeUrl = ({
  apiKey,
  analyticsTraceId,
  storefrontOrigin,
  devMode,
  phoneCapture,
  saveDiscountAt,
  flowVersion,
}: {
  apiKey: string;
  analyticsTraceId?: string;
  storefrontOrigin?: string;
  devMode?: boolean;
  phoneCapture?: boolean;
  saveDiscountAt?: string;
  flowVersion: string;
}): string => {
  /* eslint-disable @typescript-eslint/naming-convention */
  const params = new URLSearchParams({
    target_origin: window.location.origin,
    api_key: apiKey,
    flow: SHOP_DISCOUNT_FLOW,
    flow_version: flowVersion,
    // BUILD_LOCALE is used for generating localized bundles.
    // See ./scripts/i18n-dynamic-import-replacer-rollup.mjs for more info.
    // eslint-disable-next-line no-process-env
    locale: process.env.BUILD_LOCALE || I18n.getDefaultLanguage(),
    ...(analyticsTraceId && {analytics_trace_id: analyticsTraceId}),
    // eslint-disable-next-line no-warning-comments
    /*
    TODO: We need to come to a consensus on how to handle boolean query params in Pay.
    For phone_capture Pay reads the value, but for dev_mode Pay looks for the presence of
    the param. https://github.com/Shopify/shop-identity/issues/1414
    */
    ...booleanQueryParam('phone_capture', phoneCapture),
    ...(devMode && booleanQueryParam('dev_mode', devMode)),
    ...(saveDiscountAt && {save_discount_at: saveDiscountAt}),
  });
  /* eslint-enable @typescript-eslint/naming-convention */
  const authDomainCore = storefrontOrigin || window.location.origin;

  return `${authDomainCore}${AUTH_PATH_CORE}?${params}`;
};
